import { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { LockClosedIcon, ShieldCheckIcon, UserIcon } from "@heroicons/react/solid";

import { ReactComponent as LoginImage } from "../../assets/images/login.svg";
import { getApplicationLogo, getOneTimeUrl, login, sendMfa, setSessionToken } from "../../utils/api";
import { AUTH_TYPE, EMPTY_FIELDS } from "../../utils/constants";
import { DashboardContext } from "../../context/DashboardContext";
import { CLIENT_BRANDING_MAPPING, CLIENT_MAPPING } from "../../utils/utils";

import Container from "./Container";
import Input from "./Input";
import Alert, { ALERT_TYPE } from "./Alert";
import Button from "../SharedComponents/Button";
import FadingBalls from "../SharedComponents/Loading/FadingBalls";

const PENDING_SSO_TARGET_STORAGE_KEY = "pendingSSOTarget";

function Login() {
    const { authType, authClient, usernameForToken, token } = useParams();

    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [loggingInLoading, setLoggingInLoading] = useState(false);
    const [alert, setAlert] = useState("");
    const [alertType, setAlertType] = useState(ALERT_TYPE.ERROR);
    const [mfaAlert, setMfaAlert] = useState("");
    const [mfaAlertType, setMfaAlertType] = useState(ALERT_TYPE.WARNING);
    const [user, setUser] = useState("");
    const [mfa, setMfa] = useState("");
    const [sendingOption, setSendingOption] = useState("email");
    const [mfaEnabled, setMfaEnabled] = useState(false);
    const [processingMFA, setProcessingMFA] = useState(false);
    const [applicationLogo, setApplicationLogo] = useState("");
    const [loggedInState, setLoggedInState] = useState(false);
    const [tokenisedLogin, setTokenisedLogin] = useState(false);

    const context = useContext(DashboardContext);

    const navigate = useNavigate();

    const handleSuccessfulLogin = useCallback(async () => {
        setAlert("");

        if (authType && authClient) {
            try {
                // Check if authClient is in CLIENT MAPPING, then use its mapped value or default to authClient
                const client = CLIENT_MAPPING[authClient.toLowerCase()] || authClient;
                window.location.href = await handleGetOneTimeUrl(authType, client);
            } catch (err) {
                console.error("Error while getting one-time URL:", err);
                navigate("/");
                setLoggingInLoading(false);
            }
        } else {
            setLoggedInState(true);
            navigate("/");
        }
    }, [authClient, authType, navigate]);

    const handleGetOneTimeUrl = async (type, client) => {
        if (type === AUTH_TYPE.APP) {
            return await getOneTimeUrl(client);
        } else {
            setAlert("Invalid authentication type");
        }
    };

    const handleLogin = async () => {
        if (username.length > 0) {
            setLoggingInLoading(true);

            try {
                const loginResponse = await login(username, password, mfa);

                if (loginResponse.data.isSSOEnabled) {
                    if (authType && authClient) {
                        sessionStorage.setItem(
                            PENDING_SSO_TARGET_STORAGE_KEY,
                            JSON.stringify({
                                authType,
                                authClient,
                            })
                        );
                    }
                    window.open(loginResponse.data.ssoResponse, "_self");
                } else {
                    setSessionToken(loginResponse.data.sessionIdentifier);
                    await handleSuccessfulLogin();
                }
            } catch (error) {
                setAlertType(ALERT_TYPE.ERROR);

                if (error.response.data.error && error.response.data.error === "MFA is required") {
                    setMfaAlertType(ALERT_TYPE.WARNING);
                    setMfaEnabled(true);
                    setMfaAlert(
                        "You have multi-factor authentication (MFA) enabled, please request your code by email or sms"
                    );

                    setUser({
                        identifier: error.response.data.userIdentifier,
                        email: error.response.data.userEmailAddress,
                        mobile: error.response.data.userMobileNumber,
                    });

                    setProcessingMFA(true);
                } else if (error.message === "Invalid MFA code") {
                    setMfaEnabled(true);
                    setProcessingMFA(true);
                    setAlert(error.message);
                } else {
                    setAlert(error.message);
                }

                setLoggingInLoading(false);
            }
        } else {
            setAlert(EMPTY_FIELDS);
            setAlertType(ALERT_TYPE.ERROR);
        }
    };

    const handleSendMfa = async () => {
        if (user.identifier && user.identifier.length > 0) {
            try {
                await sendMfa(user.identifier, sendingOption);

                setAlert(`Your MFA code has been sent to you by ${sendingOption}`);
                setAlertType(ALERT_TYPE.SUCCESS);
                setProcessingMFA(false);
            } catch (error) {
                setAlert(error.message);
                setAlertType(ALERT_TYPE.ERROR);
                setProcessingMFA(true);
            }
        }
    };

    const handleKeyPress = (key) => {
        if (key === "Enter") handleLogin();
    };

    // Function to fetch and set the application logo based on client
    const fetchApplicationLogo = useCallback(
        async (client) => {
            const clientKey = client.toLowerCase();

            // Attempt to get the mapped logo key and fetch the logo from context branding
            const logoKey = CLIENT_BRANDING_MAPPING[clientKey];
            const brandingLogo = logoKey ? context.getImage(logoKey) : null;

            try {
                if (brandingLogo) {
                    // Set logo from branding if available
                    console.log("Using logo from branding context");
                    setApplicationLogo(brandingLogo);
                } else {
                    // Fetch logo from API as a fallback if not found in branding
                    console.log("Fetching logo from API");
                    const applicationLogoResponse = await getApplicationLogo(client);
                    setApplicationLogo(applicationLogoResponse);
                }
            } catch (error) {
                setAlert(error.message);
                setAlertType(ALERT_TYPE.ERROR);
            }
        },
        [setApplicationLogo, setAlert, setAlertType, context]
    );

    useEffect(() => {
        if (context.loggedIn && !usernameForToken && !authClient) {
            handleSuccessfulLogin();
        }
    }, [authClient, context.loggedIn, handleSuccessfulLogin, usernameForToken]);

    useEffect(() => {
        context.setLoggedIn(loggedInState);
    }, [loggedInState, context]);

    useEffect(() => {
        if (authType && authClient && !context.brandingLoading) {
            fetchApplicationLogo(authClient);
        }
    }, [authType, authClient, fetchApplicationLogo, context.brandingLoading]);

    useEffect(() => {
        let isMounted = true;

        const handleTokenisedLogin = async () => {
            try {
                const loginResponse = await login(usernameForToken, token);

                if (isMounted) {
                    setSessionToken(loginResponse.data.sessionIdentifier);
                    await handleSuccessfulLogin();
                }
            } catch (error) {
                if (isMounted) {
                    setAlertType(ALERT_TYPE.ERROR);
                    setAlert(error.message);
                }
            }
        };

        if (usernameForToken && token) {
            setLoggingInLoading(true);
            setTokenisedLogin(true);

            setTimeout(async () => {
                if (isMounted) await handleTokenisedLogin();
            }, 2500);
        }

        return () => {
            isMounted = false;
        };
    }, [handleSuccessfulLogin, token, usernameForToken, loggedInState, navigate]);

    return tokenisedLogin ? (
        <FadingBalls
            text={"Authenticating you..."}
            alert={alert}
            Alert={<Alert marginLess text={alert} type={alertType} active={alert} close={() => setAlert("")} />}
        />
    ) : (
        <Container>
            <div className="hidden lg:flex items-center justify-center w-1/2 p-8 bg-gradient-to-l from-primary-50 to-secondary-50">
                <LoginImage className="h-56 w-full sm:h-72 md:h-80 lg:w-full" />
            </div>
            <div className="lg:w-1/2 md:full p-8 grid grid-flow-row gap-6">
                <div className="grid-flow-row grid gap-4">
                    <div className="flex items-start justify-between">
                        <h1 className="font-semibold tracking-tight text-3xl text-indigo-900 text-left">Login</h1>
                    </div>
                </div>
                <Alert text={alert} type={alertType} active={alert} close={() => setAlert("")} />
                <div className="flex">
                    <form className="w-full">
                        <div className="grid grid-flow-row gap-4">
                            {applicationLogo && authClient !== "ace 2.0 Survey Platform" && (
                                <img alt="application logo" className="w-40" src={applicationLogo} />
                            )}
                            <Input
                                id="username"
                                type="text"
                                name="Username"
                                placeholder="Enter username or email"
                                onChange={setUsername}
                                onKeyPress={handleKeyPress}
                                Icon={<UserIcon className="h-4 w-4 text-gray-500" />}
                                disabled={processingMFA || loggingInLoading}
                            />
                            <Input
                                id="password"
                                type="password"
                                name="Password"
                                placeholder="Enter password"
                                onChange={setPassword}
                                onKeyPress={handleKeyPress}
                                Icon={<LockClosedIcon className="h-4 w-4 text-gray-500" />}
                                disabled={processingMFA || loggingInLoading}
                            />
                        </div>
                        <Alert
                            text={
                                "If you have not previously set up a password, please use the Forgot Password option to set up a new password, and then return to this login page to log in with your email and new password."
                            }
                            type={ALERT_TYPE.NEUTRAL}
                            active={true && authClient === 'unitingagewell'} // can be expanded to other clients in the future
                        />
                        <div className="text-right mt-2">
                            <a href={"/forgot-password"} className="text-primary-600 font-medium text-sm">
                                Forgot your password?
                            </a>
                        </div>
                        <div className={mfaEnabled ? "mt-8" : ""}>
                            {mfaEnabled && (
                                <label className="text-sm font-medium text-indigo-900 w-full">
                                    Multi-factor authentication (MFA)
                                </label>
                            )}
                            <Alert text={mfaAlert} type={mfaAlertType} active={mfaAlert} />
                            {mfaEnabled && (
                                <div className="grid grid-flow-row gap-3 mt-6">
                                    <Input
                                        id="mfa"
                                        type="text"
                                        name="MFA code"
                                        placeholder="Enter MFA code"
                                        onChange={setMfa}
                                        Icon={<ShieldCheckIcon className="h-4 w-4 text-gray-500" />}
                                    />
                                </div>
                            )}
                        </div>
                    </form>
                </div>
                {mfaEnabled && (
                    <div className="flex flex-col gap-2">
                        <label htmlFor="sending-option" className="text-sm font-medium text-indigo-900 w-full">
                            Send MFA code using:
                        </label>
                        <select
                            id="mfaOption"
                            name="Sending Option"
                            className={
                                "bg-blue-gray-50 border " +
                                "border-gray-300 rounded-md shadow-sm pl-3 pr-10 py-2 " +
                                "text-left focus:outline-none focus:ring-1 " +
                                "focus:ring-primary-600 focus:border-primary-600 sm:text-sm w-full"
                            }
                            onChange={(e) => setSendingOption(e.target.value)}
                        >
                            {user.email && (
                                <option key="1" value="email" className={"mt-1"}>
                                    Email: {user.email}
                                </option>
                            )}
                            {user.mobile && (
                                <option key="2" value="sms" className={"mt-1"}>
                                    SMS: {user.mobile}
                                </option>
                            )}
                        </select>
                    </div>
                )}
                <div className="flex flex-col gap-2">
                    {processingMFA ? (
                        <button
                            onClick={() => handleSendMfa()}
                            className={
                                "block w-full px-3 py-3 font-medium rounded-md focus:outline-none " +
                                "button hover:bg-primary-700 text-base text-white focus:bg-primary-700 " +
                                "text-white focus:ring-inset focus:ring-1 bg-primary-600 hover:bg-primary-700 " +
                                "focus:outline-none focus:ring-primary-600"
                            }
                        >
                            Send code
                        </button>
                    ) : (
                        <Button
                            text={loggingInLoading ? "Logging in" : "Login"}
                            onClick={handleLogin}
                            extraStyling={
                                "w-full px-3 py-3 font-medium rounded-md focus:outline-none " +
                                "button hover:bg-primary-700 text-base text-white focus:bg-primary-700 " +
                                "text-white focus:ring-inset focus:ring-1 bg-primary-600 hover:bg-primary-700 " +
                                "focus:outline-none focus:ring-primary-600 justify-center h-12"
                            }
                            loading={loggingInLoading}
                            disabled={loggingInLoading}
                        />
                    )}
                    {processingMFA && (
                        <div className={"text-right"}>
                            <a href={"/"} className="text-primary-600 font-medium text-sm">
                                Cancel and login again
                            </a>
                        </div>
                    )}
                </div>
            </div>
        </Container>
    );
}

export default Login;
